import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams, type Href } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';

import { MessageList } from './message-list';
import { STATUS_ICON } from './message-status';

import { ActivityIndicator, Text, useTheme } from '@/components/ui';
import { useMessages } from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import type { InboxMessage } from '@/services/data-service';
import {
	INBOX_PLACE_LABEL, type InboxPlace, NEEDS_HUMAN_FILTER, PLACE_STATUS,
} from '@/utils/inbox-triage';

/**
 * The inbox list, for whichever PLACE it is rendered under (#369).
 *
 * One component, six routes — `/inbox`, `/inbox/mine`, `/inbox/triage`, `/inbox/waiting`,
 * `/inbox/spam`, `/inbox/archived`. The place comes in as a PROP from the route file rather
 * than being read out of the URL here, so there is exactly one thing that decides which
 * view this is and it is the route itself.
 *
 * Filters stay query params (`?label=`, `?associationId=`, `?q=`, `?showFiled=`): they
 * narrow whatever place you are in, and several can apply at once.
 */

/** What an empty place means — different reassurance for each. */
const PLACE_EMPTY_STATE: Record<InboxPlace, string> = {
	all: 'Nothing here.',
	mine: 'Nothing is assigned to you.',
	triage: 'Everything has been filed. Nothing needs attention.',
	archived: 'Nothing has been archived.',
	waiting: 'Nobody is waiting on an answer from a homeowner.',
	spam: 'No mail has been marked as spam.',
};

const useDebounced = (value: string, delayMs: number): string => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(t);
	}, [value, delayMs]);
	return debounced;
};

export const InboxListScreen: React.FC<{ place: InboxPlace }> = ({ place }) => {
	const theme = useTheme();
	const { canUseInbox } = usePermissions();
	/**
	 * LOCAL params, deliberately — this is a screen, so these are its own route's and they
	 * are reliable. The rail and the drawer cannot use this hook (a layout's local params
	 * are its own segment's), which is precisely why the PLACE is a route and not a param.
	 */
	const params = useLocalSearchParams<{
		label?: string; q?: string; withoutRequest?: string; associationId?: string;
		showFiled?: string;
	}>();

	/**
	 * Filed mail is opt-IN. The inbox is a work queue first: by the time a message has
	 * produced a work order or been reviewed, it is history, and leaving it in the default
	 * view buries the rows that still need someone.
	 */
	const showFiled = params.showFiled === '1';
	const associationId = typeof params.associationId === 'string' ? params.associationId : undefined;
	const withoutRequest = params.withoutRequest === '1';
	/**
	 * The triage PLACE is the needs-human label filter, portfolio-wide. A per-association
	 * triage view is a different thing — `/inbox?associationId=X&label=needs-human` — and
	 * stays a filter, because it narrows All mail rather than being somewhere else.
	 */
	const label = place === 'triage'
		? NEEDS_HUMAN_FILTER
		: (typeof params.label === 'string' ? params.label : undefined);

	// Local state keeps typing responsive (CLAUDE.md "Stable Search Inputs"); the URL is
	// updated on the debounce so a search stays bookmarkable.
	const initialQuery = typeof params.q === 'string' ? params.q : '';
	const [searchQuery, setSearchQuery] = useState(initialQuery);
	const debouncedSearch = useDebounced(searchQuery.trim(), 300);

	useEffect(() => {
		if (debouncedSearch !== initialQuery) router.setParams({ q: debouncedSearch || undefined });
	}, [debouncedSearch, initialQuery]);

	/** The away-places are a message STATUS; `mine` and `triage` are ordinary filters. */
	const status = PLACE_STATUS[place];
	const isAway = !!status;

	// Memoized: the query object is part of the cache key.
	const query = useMemo(
		() => ({
			label,
			associationId,
			// `me` is resolved server-side against the caller, so the URL stays shareable.
			assignedTo: place === 'mine' ? 'me' : undefined,
			q: debouncedSearch || undefined,
			withoutRequest: withoutRequest || undefined,
			status,
			/**
			 * `withoutRequest` is the stricter, improvement-specific form of the same
			 * question; layering both would be redundant, so it wins where it is set.
			 * Anything in an away-place has been dealt with by definition, so the filter is
			 * meaningless there and would hide most of the view.
			 */
			unfiled: (!showFiled && !withoutRequest && !isAway) || undefined,
			limit: 100,
		}),
		[label, associationId, place, debouncedSearch, withoutRequest, showFiled, status, isAway],
	);
	const { data, isLoading, isFetching } = useMessages(query, { enabled: canUseInbox });

	/**
	 * The list's OWN total, not the label registry's: taken from the same query that
	 * produced these rows, so the number in the banner cannot disagree with the number of
	 * rows underneath it.
	 */
	const queueCount = data?.pagination.total ?? 0;

	if (!canUseInbox) return null;

	return (
		<View style={{ flex: 1, minWidth: 0 }} testID={`inbox-place-${place}`}>
			<View
				style={{
					flexDirection: 'row',
					alignItems: 'center',
					height: 52,
					paddingHorizontal: 16,
					backgroundColor: theme.colors.surface,
					borderBottomWidth: 1,
					borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
				}}
			>
				<Ionicons name="search" size={18} color={theme.colors.onSurfaceVariant} style={{ marginRight: 10 }} />
				<TextInput
					placeholder="Search mail"
					placeholderTextColor={theme.colors.onSurfaceVariant}
					onChangeText={setSearchQuery}
					value={searchQuery}
					style={{ flex: 1, height: '100%', paddingLeft: 6, fontSize: 15, color: theme.colors.onSurface }}
					testID="inbox-search-input"
				/>
				{isFetching && !isLoading ? <ActivityIndicator size="small" style={{ marginLeft: 10 }} /> : null}
				{/*
					The inbox defaults to WORK — mail nothing has been done with. Filed mail is
					still reachable, but it is history, and leaving it in by default buries the
					handful of rows that actually need someone under everything already handled.
					Written to the URL so the wider view stays shareable.

					Meaningless in an away-place — everything there is filed by definition.
				*/}
				{isAway ? null : (
					<Pressable
						onPress={() => router.setParams({ showFiled: showFiled ? undefined : '1' })}
						testID="btn-toggle-show-filed"
						style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 8, marginLeft: 8 }}
					>
						<Ionicons
							name={showFiled ? 'checkbox' : 'square-outline'}
							size={16}
							color={showFiled ? theme.colors.primary : theme.colors.onSurfaceVariant}
							style={{ marginRight: 6 }}
						/>
						<Text style={{ fontSize: 13, fontWeight: '600', color: showFiled ? theme.colors.primary : theme.colors.onSurfaceVariant }}>
							Show filed
						</Text>
					</Pressable>
				)}
			</View>

			{/*
				Say plainly that this is NOT the working inbox. These rows look like any other
				row, and someone arriving from the rail needs to know why the mail they were
				expecting is absent — and how to put a message back.
			*/}
			{status ? (
				<View
					testID="archived-banner"
					style={{
						flexDirection: 'row',
						alignItems: 'center',
						paddingHorizontal: 16,
						paddingVertical: 10,
						backgroundColor: theme.colors.surfaceVariant,
						borderBottomWidth: 1,
						borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
					}}
				>
					<Ionicons name={STATUS_ICON[status]} size={18} color={theme.colors.onSurfaceVariant} style={{ marginRight: 10 }} />
					<Text style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.colors.onSurfaceVariant }}>
						{queueCount > 0
							? `${queueCount} message${queueCount === 1 ? '' : 's'} in ${INBOX_PLACE_LABEL[place]}. `
								+ 'Open one and choose "Move to inbox" to bring it back.'
							: PLACE_EMPTY_STATE[place]}
					</Text>
				</View>
			) : null}

			{/*
				The triage queue explains ITSELF. Staff arrive here from a red badge, and a bare
				list of mail does not say why these are special or what clears them — so state
				the action, and state the count so it can be checked against the badge.
			*/}
			{withoutRequest ? (
				<View
					testID="without-request-banner"
					style={{
						flexDirection: 'row',
						alignItems: 'center',
						paddingHorizontal: 16,
						paddingVertical: 10,
						backgroundColor: theme.colors.errorContainer,
						borderBottomWidth: 1,
						borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
					}}
				>
					<Ionicons name="mail-unread" size={18} color={theme.colors.error} style={{ marginRight: 10 }} />
					<Text style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.colors.onErrorContainer }}>
						{queueCount > 0
							? `${queueCount} submission${queueCount === 1 ? '' : 's'} with no request yet. `
								+ 'Open one and choose "Create request" so it appears under Exterior Improvements.'
							: 'Every submission has a request.'}
					</Text>
				</View>
			) : null}

			{place === 'triage' ? (
				<View
					testID="needs-human-banner"
					style={{
						flexDirection: 'row',
						alignItems: 'center',
						paddingHorizontal: 16,
						paddingVertical: 10,
						backgroundColor: theme.colors.errorContainer,
						borderBottomWidth: 1,
						borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
					}}
				>
					<Ionicons name="alert-circle" size={18} color={theme.colors.error} style={{ marginRight: 10 }} />
					<Text style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.colors.onErrorContainer }}>
						{queueCount > 0
							? `${queueCount} message${queueCount === 1 ? '' : 's'} could not be matched to an owner. `
								+ 'Open one and assign it to a lot to file it.'
							: PLACE_EMPTY_STATE.triage}
					</Text>
				</View>
			) : null}

			<MessageList
				messages={data?.data ?? []}
				onSelect={(message: InboxMessage) => router.push(`/inbox/${message.id}` as Href)}
				emptyText={debouncedSearch ? 'Nothing matches your search.' : PLACE_EMPTY_STATE[place]}
				loading={isLoading}
			/>
		</View>
	);
};

export default InboxListScreen;
